/**
 * Turns an audio or video file into the two things Rey can actually read, in the browser, before
 * anything is uploaded:
 *
 *   - a 16 kHz mono WAV of the speech, which is what the transcription endpoint wants
 *   - a handful of JPEG frames, which feed the multimodal image path
 *
 * WHY THIS EXISTS. Uploading the source file does not work, and not because of the upload: the
 * transcription service answers 400 "Maximum file size exceeded (parameter=audio_filesize_mb)" — a 50 MB
 * video was refused. A video is mostly video; its speech, as mono 16 kHz PCM, is a hundredth of that.
 * Extracting here means a 1.4 GB recording leaves the browser as a few MB, and it keeps the VISUAL
 * content too, which extracting audio alone would throw away.
 *
 * No dependency, deliberately. FusionWeb has 27 npm packages and none of them touch media — the mic
 * button does its capture with getUserMedia, MediaRecorder and AudioContext. ffmpeg.wasm would have added
 * ~25 MB to solve what decodeAudioData and a canvas already do.
 */

// A plain .js module has no component context, so user-facing notes/progress labels resolve through
// i18n.global.t() — the same pattern the store's actions and GlobalsPlugin use.
import i18n from '../i18n';

/** Sample rate the transcription endpoint expects. Speech carries no useful energy above 8 kHz. */
export const TARGET_SAMPLE_RATE = 16000;

/**
 * Frames sampled from a video. Matches the server's per-turn image cap
 * (ThreadAttachmentResolver.MaxImages) — sampling more would upload images the resolver then drops in
 * silence, which is the failure this whole session has been about.
 */
export const MAX_FRAMES = 4;

/** Above this, decodeAudioData is a memory risk: the whole file has to sit in an ArrayBuffer. */
export const MAX_DECODE_BYTES = 500 * 1024 * 1024;

/**
 * Largest WAV this will produce in one piece. Longer recordings are split across several.
 *
 * The transcription service refuses anything over about 24 MB (MediaTranscriptionWorker.SttMaxBytes),
 * and 16 kHz mono 16-bit PCM runs at 32 kB per second — so a single file hits that ceiling at 13 minutes
 * 6 seconds. Without splitting, every recording longer than that came back with no transcript at all: a
 * sales call, a training session and a long walkaround are all normally longer than 13 minutes.
 *
 * Set below the service limit rather than at it, to leave room for the header and for the service
 * measuring slightly differently than we do. If SttMaxBytes ever moves, this should move with it.
 */
export const MAX_WAV_BYTES = 20 * 1024 * 1024;

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm'];

export function isVideoFile(name) {
  const lower = String(name || '').toLowerCase();
  return VIDEO_EXTENSIONS.some(ext => lower.endsWith(ext));
}

/**
 * Extracts speech and frames from one media file.
 *
 * Returns { files, notes }. `files` are the derived File objects to upload in place of the source.
 * `notes` explain anything that could not be produced — surfaced to the user rather than swallowed,
 * because "the video was attached but its audio silently wasn't" is indistinguishable from success.
 *
 * Never throws: a browser that cannot decode a particular container should cost the user the extraction,
 * not the message they were trying to send.
 */
export async function extractMedia(file, { onProgress } = {}) {
  const files = [];
  const notes = [];

  if (!file) return { files, notes };

  if (file.size > MAX_DECODE_BYTES) {
    // Name the limit, not just the verdict: the fix is to shorten or re-export the recording, and the
    // user cannot judge by how much without knowing the ceiling. Decoding needs the whole file in one
    // ArrayBuffer, so this is about the SOURCE size, independent of how long the recording is.
    notes.push(i18n.global.t('messaging.attachments.extraction.fileTooLarge', {
      fileName: file.name,
      size: mb(file.size),
      max: mb(MAX_DECODE_BYTES),
    }));
    return { files, notes };
  }

  const baseName = stripExtension(file.name);

  if (isVideoFile(file.name)) {
    if (onProgress) onProgress(i18n.global.t('messaging.attachments.extraction.extractingFrames'));
    try {
      const frames = await extractFrames(file, baseName);
      files.push(...frames);
      if (frames.length === 0) notes.push(i18n.global.t('messaging.attachments.extraction.noFrames', { fileName: file.name }));
    } catch (e) {
      notes.push(i18n.global.t('messaging.attachments.extraction.framesFailed', { fileName: file.name }));
    }
  }

  if (onProgress) onProgress(i18n.global.t('messaging.attachments.extraction.extractingAudio'));
  try {
    const audio = await extractAudioAsWav(file, baseName);
    if (audio.length) {
      files.push(...audio);
      // Say so when it was split. Several audio chips appearing from one video otherwise looks like a
      // bug, and the user should know the transcript arrives in pieces.
      if (audio.length > 1) {
        notes.push(i18n.global.t('messaging.attachments.extraction.splitIntoParts', { fileName: file.name, count: audio.length }));
      }
    } else {
      notes.push(i18n.global.t('messaging.attachments.extraction.noAudioTrack', { fileName: file.name }));
    }
  } catch (e) {
    // The most likely cause is a container this browser cannot decode. Say so: without the audio there
    // is no transcript, and a user told nothing would assume Rey simply ignored the recording.
    notes.push(i18n.global.t('messaging.attachments.extraction.audioUnsupported', { fileName: file.name }));
  }

  return { files, notes };
}

/**
 * Decodes the file's audio track and re-renders it as 16 kHz mono WAV.
 *
 * decodeAudioData handles the demuxing, including for video containers — which is why no demuxer library
 * is needed. OfflineAudioContext then does the resample and the downmix in one render, faster than real
 * time; routing through MediaRecorder instead would take as long as the recording plays.
 *
 * Returns an ARRAY of files: anything over MAX_WAV_BYTES is split, because the transcription service
 * refuses a single file that large and would otherwise return nothing for the whole recording.
 */
export async function extractAudioAsWav(file, baseName) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  if (!AudioCtx || !OfflineCtx) return [];

  const buffer = await file.arrayBuffer();

  const decodeCtx = new AudioCtx();
  let decoded;
  try {
    decoded = await decodeCtx.decodeAudioData(buffer);
  } finally {
    // Browsers cap concurrent AudioContexts; leaking one per attachment exhausts the budget.
    if (decodeCtx.state !== 'closed') await decodeCtx.close().catch(() => {});
  }

  if (!decoded || !decoded.duration) return [];

  const frameCount = Math.ceil(decoded.duration * TARGET_SAMPLE_RATE);
  const offline = new OfflineCtx(1, frameCount, TARGET_SAMPLE_RATE);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();
  const rendered = await offline.startRendering();

  return segmentToWavFiles(rendered.getChannelData(0), baseName);
}

/**
 * Wraps rendered mono samples as one or more WAV files, none exceeding MAX_WAV_BYTES.
 *
 * Split on a plain byte budget rather than on silence: finding sentence boundaries would need speech
 * detection we do not have here, and a cut mid-word costs one word at the seam, whereas not splitting
 * at all costs the entire transcript. Parts are named so their order is legible to whoever — or
 * whatever — reads the transcripts back.
 */
export function segmentToWavFiles(samples, baseName) {
  const samplesPerSegment = Math.floor(MAX_WAV_BYTES / 2);

  if (samples.length <= samplesPerSegment) {
    // Unsplit files keep the original name: the common case should not look like a special case.
    const wav = encodeWav(samples, TARGET_SAMPLE_RATE);
    return [new File([wav], `${baseName}-audio.wav`, { type: 'audio/wav' })];
  }

  const total = Math.ceil(samples.length / samplesPerSegment);
  const files = [];
  for (let i = 0; i < total; i += 1) {
    const slice = samples.subarray(i * samplesPerSegment, (i + 1) * samplesPerSegment);
    const wav = encodeWav(slice, TARGET_SAMPLE_RATE);
    files.push(new File([wav], `${baseName}-audio-part${i + 1}of${total}.wav`, { type: 'audio/wav' }));
  }
  return files;
}

/**
 * Samples frames evenly across the video.
 *
 * Evenly rather than from the opening seconds: the first frames of a screen recording are a title card or
 * a desktop, and a walkaround starts on the ground. Spreading them is what makes four images represent
 * the recording instead of its first two seconds.
 */
export async function extractFrames(file, baseName, count = MAX_FRAMES) {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  video.preload = 'metadata';
  video.src = url;

  try {
    await once(video, 'loadedmetadata');
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    if (!duration) return [];

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');

    const frames = [];
    for (let i = 0; i < count; i += 1) {
      // Offset by half a step so the first sample is inside the content rather than on frame zero,
      // which is routinely black.
      const at = (duration * (i + 0.5)) / count;
      video.currentTime = at;
      await once(video, 'seeked');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await toBlob(canvas);
      if (blob) {
        frames.push(new File([blob], `${baseName}-frame${i + 1}.jpg`, { type: 'image/jpeg' }));
      }
    }
    return frames;
  } finally {
    video.removeAttribute('src');
    URL.revokeObjectURL(url);
  }
}

// ── helpers ─────────────────────────────────────────────────────────────────────────────────────

function once(element, event, timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error(`timed out waiting for ${event}`));
    }, timeoutMs);
    function cleanup() {
      clearTimeout(timer);
      element.removeEventListener(event, onEvent);
      element.removeEventListener('error', onError);
    }
    function onEvent() { cleanup(); resolve(); }
    function onError() { cleanup(); reject(new Error(`${event} failed`)); }
    element.addEventListener(event, onEvent, { once: true });
    element.addEventListener('error', onError, { once: true });
  });
}

function toBlob(canvas) {
  return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.8));
}

function stripExtension(name) {
  const dot = String(name || '').lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : String(name || 'media');
}

function mb(bytes) {
  return Math.round((Number(bytes) || 0) / (1024 * 1024));
}

/**
 * Minimal 16-bit PCM WAV. Written rather than imported: it is a 44-byte header, and every library that
 * does it brings a decoder nobody here needs.
 */
export function encodeWav(samples, sampleRate) {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeAscii(view, 8, 'WAVE');
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);          // PCM chunk size
  view.setUint16(20, 1, true);           // PCM
  view.setUint16(22, 1, true);           // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true);           // block align
  view.setUint16(34, 16, true);          // bits per sample
  writeAscii(view, 36, 'data');
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i += 1) {
    // Clamp before scaling: a value outside [-1, 1] wraps rather than saturating, which reads as a burst
    // of noise exactly where the recording was loudest.
    const clamped = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
    offset += 2;
  }

  return new Blob([view], { type: 'audio/wav' });
}

function writeAscii(view, offset, text) {
  for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
}
